import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, CheckCircle, XCircle, Image as ImageIcon, Loader2 } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import GalleryThemeToggle from "../components/GalleryThemeToggle";
import ShopFooter from "../components/ShopFooter";
import { toast } from "sonner";
import { ShopLanguageSelector } from "../components/ShopLanguageSelector";
import { DEFAULT_CURRENCY } from "@shared/const";

interface GallerySession {
  projectId: number;
  projectTitle: string;
  cartId: number;
  token: string;
  photographerId?: number;
  selectedPackageId?: number | null;
}

export default function GalleryCheckout() {
  const [, navigate] = useLocation();
  const { t, language } = useTranslation();
  const [session, setSession] = useState<GallerySession | null>(null);
  const [deliveryType, setDeliveryType] = useState<"delivery" | "pickup">("delivery");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Bosnien und Herzegowina");
  const [phone, setPhone] = useState("");
  const [differentBillingAddress, setDifferentBillingAddress] = useState(false);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingPostalCode, setBillingPostalCode] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingCountry, setBillingCountry] = useState("Bosnien und Herzegowina");
  const [paypalClientId, setPaypalClientId] = useState<string>("");
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [photographerId, setPhotographerId] = useState<number | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    const sessionData = localStorage.getItem("gallery_session");
    if (!sessionData) {
      navigate("/gallery/login");
      return;
    }

    try {
      const parsed = JSON.parse(sessionData);
      setSession(parsed);
      setSelectedPackageId(parsed.selectedPackageId ?? null);
      setPhotographerId(parsed.photographerId ?? null);
    } catch (error) {
      navigate("/gallery/login");
    }
  }, [navigate]);

  const { data: cartData } = trpc.gallery.getCart.useQuery(
    {
      cartId: session?.cartId || 0,
      token: session?.token || "",
    },
    {
      enabled: !!session,
    }
  );

  // If all items are digital (no prints), default to self pickup
  useEffect(() => {
    if (cartData?.items && cartData.items.length > 0) {
      const hasPrint = (cartData.items as any[]).some((it) => it.productType === 'print');
      if (!hasPrint) {
        setDeliveryType('pickup');
      }
    }
  }, [cartData]);

  const hasPrint = (cartData?.items || []).some((it: any) => it.productType === 'print');

  // Resolve selected package info to display price
  const { data: activePackages } = trpc.packages.listActive.useQuery(
    photographerId ? { photographerId } : (undefined as any),
    { enabled: !!photographerId }
  );
  const selectedPackage = (selectedPackageId && activePackages)
    ? (activePackages as any[]).find((p) => p.id === selectedPackageId) || null
    : null;

  // Get PayPal settings from photographer (public endpoint, no auth required)
  const { data: settings } = trpc.settings.getPublicPayPalSettings.useQuery(
    { photographerId: photographerId || 0 },
    { enabled: !!photographerId }
  );

  useEffect(() => {
    if (settings?.paypalClientId) {
      setPaypalClientId(settings.paypalClientId);
    }
  }, [settings]);

  // Get available countries for shipping (only active ones)
  const { data: availableCountries } = trpc.gallery.getAvailableCountries.useQuery(
    { photographerId: photographerId || 0, token: session?.token || "" },
    { enabled: !!photographerId && !!session?.token }
  );

  // Auto-select first available country if current country is not in list
  useEffect(() => {
    if (availableCountries && availableCountries.length > 0) {
      if (!availableCountries.includes(country)) {
        setCountry(availableCountries[0]);
      }
    }
  }, [availableCountries, country]);

  // Get shipping cost for selected country from backend
  const { data: shippingData } = trpc.gallery.getShippingCost.useQuery(
    {
      photographerId: photographerId || 0,
      country: country,
      token: session?.token || "",
    },
    {
      enabled: !!photographerId && !!session?.token && deliveryType === "delivery" && hasPrint,
    }
  );

  const shippingAmount = deliveryType === "delivery" && hasPrint && shippingData 
    ? parseFloat(shippingData.cost || "0") 
    : 0;

  const createPayPalOrderMutation = trpc.paypal.createOrder.useMutation();
  const utils = trpc.useUtils();

  const currency = settings?.currency || DEFAULT_CURRENCY;
  const pricePerImage = parseFloat(settings?.pricePerImage || "10.00");
  const subtotal = selectedPackage
    ? parseFloat(selectedPackage.price || "0")
    : (cartData?.items.length || 0) * pricePerImage;
  const totalAmount = subtotal + shippingAmount;
  const formatAmount = useMemo(() => (value: number) => `${value.toFixed(2)} ${currency}`, [currency]);

  const handlePayPalCheckout = async () => {
      console.log("[Checkout] Starting PayPal checkout...");
    setIsProcessingPayment(true);
    
    if (!session || !customerName || !customerEmail || !phone) {
      toast.error(t.validation.required);
      setIsProcessingPayment(false);
      return;
    }

    // Validate address fields only for delivery
    if (deliveryType === "delivery" && (!addressLine1 || !postalCode || !city || !country)) {
      toast.error(t.validation.required);
      setIsProcessingPayment(false);
      return;
    }

    // Validate billing address if different
    if (differentBillingAddress && (!billingAddressLine1 || !billingPostalCode || !billingCity || !billingCountry)) {
      toast.error(t.validation.required);
      setIsProcessingPayment(false);
      return;
    }

    try {
      // Create PayPal order
            console.log("[Checkout] Creating PayPal order...", {
              cartId: session.cartId,
              deliveryType,
              country,
              shippingAmount,
            });
        const result = await createPayPalOrderMutation.mutateAsync({
        cartId: session.cartId,
        customerName,
        customerEmail,
        customerAddressLine1: deliveryType === "delivery" ? addressLine1 : t.checkout.pickupAddress,
        customerAddressLine2: deliveryType === "delivery" ? (addressLine2 || undefined) : undefined,
        customerPostalCode: deliveryType === "delivery" ? postalCode : "-",
        customerCity: deliveryType === "delivery" ? city : "-",
        customerCountry: deliveryType === "delivery" ? country : "-",
        customerPhone: phone,
        billingAddressLine1: differentBillingAddress ? billingAddressLine1 : undefined,
        billingAddressLine2: differentBillingAddress ? (billingAddressLine2 || undefined) : undefined,
        billingPostalCode: differentBillingAddress ? billingPostalCode : undefined,
        billingCity: differentBillingAddress ? billingCity : undefined,
        billingCountry: differentBillingAddress ? billingCountry : undefined,
        deliveryType,
        language: language as "de" | "en" | "hr",
        selectedPackageId: selectedPackageId || undefined,
        shippingCost: deliveryType === "delivery" && hasPrint ? shippingAmount : undefined,
      });
      console.log("[Checkout] Order created:", result);

      setOrderId(result.orderId);
              console.log("[Checkout] Saved to localStorage:", {
                orderId: result.orderId,
                paypalOrderId: result.paypalOrderId,
              });
      // Persist ids for return page to capture
      try {
        localStorage.setItem('pending_order_id', String(result.orderId));
        if (result.paypalOrderId) {
          localStorage.setItem('pending_paypal_order_id', String(result.paypalOrderId));
        }
      } catch {}
      // Only redirect to official PayPal domains. Guards against a compromised
      // server or MITM response substituting an attacker-controlled URL.
      const isTrustedPaypalUrl = (raw: string): boolean => {
        try {
          const u = new URL(raw);
          if (u.protocol !== "https:") return false;
          return u.hostname === "www.paypal.com" ||
                 u.hostname === "www.sandbox.paypal.com" ||
                 u.hostname.endsWith(".paypal.com");
        } catch {
          return false;
        }
      };

      if (result.approvalUrl && isTrustedPaypalUrl(result.approvalUrl as string)) {
        console.log("[Checkout] Redirecting to PayPal:", result.approvalUrl);
        window.location.href = result.approvalUrl as string;
      } else {
        if (result.approvalUrl) {
          console.warn("[Checkout] Rejected untrusted approvalUrl:", result.approvalUrl);
        }
        const paypalMode = settings?.paypalMode || 'sandbox';
        const paypalBaseUrl = paypalMode === 'sandbox'
          ? 'https://www.sandbox.paypal.com'
          : 'https://www.paypal.com';
        window.location.href = `${paypalBaseUrl}/checkoutnow?token=${encodeURIComponent(String(result.paypalOrderId))}`;
      }
    } catch (error) {
      console.error("Failed to create PayPal order:", error);
      toast.error(t.checkout.paymentFailed);
      setIsProcessingPayment(false);
    }
  };

  if (!session) {
    return null;
  }

  if (orderCompleted && orderId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl">{t.checkout.orderSuccess}</CardTitle>
            <CardDescription>{t.checkout.orderSuccessDesc}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t.orders.orderNumber}</span>
                <span className="font-mono font-semibold">#{orderId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t.cart.total}</span>
                <span className="font-semibold">{formatAmount(totalAmount)}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {t.checkout.downloadInfoSent}
            </p>
            <Button onClick={() => navigate("/gallery")} className="w-full">
              {t.common.backToGallery}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 md:gap-4">
              <Button variant="ghost" onClick={() => navigate("/gallery/cart")}> 
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.common.back}
              </Button>
              <BrandLogo size={32} rounded showName className="md:block" />
              <h1 className="text-lg md:text-xl font-semibold">{t.checkout.title}</h1>
            </div>
            <div className="flex items-center gap-2 justify-end md:justify-start">
              <ShopLanguageSelector />
              {session && <GalleryThemeToggle cartId={session.cartId} token={session.token} />}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 flex-1">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Customer Info - wird nicht geändert, zu lang zum Kopieren */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>{t.checkout.customerInfo}</CardTitle>
                <CardDescription>{t.checkout.customerInfoDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label htmlFor="name">{t.checkout.name}</Label>
                  <Input
                    id="name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={t.checkout.namePlaceholder}
                    required
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="email">{t.checkout.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder={t.checkout.emailPlaceholder}
                    required
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="phone">{t.checkout.phone}</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t.checkout.phonePlaceholder}
                    required
                  />
                </div>

                {/* Delivery Type Selection */}
                <div className="space-y-3 border-t pt-4">
                  <Label>{t.checkout.deliveryType || "Lieferart"}</Label>
                  {hasPrint ? (
                    <RadioGroup
                      value={deliveryType}
                      onValueChange={(value) => setDeliveryType(value as "delivery" | "pickup")}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="delivery" id="delivery" />
                        <Label htmlFor="delivery" className="cursor-pointer font-normal">
                          {t.checkout.homeDelivery || "Lieferung zur Adresse"}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pickup" id="pickup" />
                        <Label htmlFor="pickup" className="cursor-pointer font-normal">
                          {t.checkout.selfPickup || "Abholung"}
                        </Label>
                      </div>
                    </RadioGroup>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      {t.checkout.digitalDeliveryOnly}
                    </div>
                  )}
                </div>

                {/* Address fields - nur bei delivery */}
                {deliveryType === "delivery" && (
                  <>
                    <div className="space-y-3">
                      <Label htmlFor="addressLine1">{t.checkout.addressLine1}</Label>
                      <Input
                        id="addressLine1"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                        placeholder={t.checkout.addressLine1Placeholder || t.checkout.addressLine1}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="addressLine2">{t.checkout.addressLine2} ({t.common.optional})</Label>
                      <Input
                        id="addressLine2"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                        placeholder={t.checkout.addressLine2Placeholder || t.checkout.addressLine2}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <Label htmlFor="postalCode">{t.checkout.postalCode}</Label>
                        <Input
                          id="postalCode"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          placeholder={t.checkout.postalCodePlaceholder}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="city">{t.checkout.city}</Label>
                        <Input
                          id="city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder={t.checkout.cityPlaceholder}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="country">{t.checkout.country}</Label>
                      <Select value={country} onValueChange={setCountry}>
                        <SelectTrigger id="country">
                          <SelectValue placeholder={t.checkout.countryPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCountries && availableCountries.length > 0 ? (
                            availableCountries.map((countryName) => (
                              <SelectItem key={countryName} value={countryName}>
                                {countryName}
                              </SelectItem>
                            ))
                          ) : (
                            Array.isArray(t.settings.shippingRatesCountryOptions) ? (
                              t.settings.shippingRatesCountryOptions.map((country: string) => (
                                <SelectItem key={country} value={country}>{country}</SelectItem>
                              ))
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Versandkosten - automatisch berechnet basierend auf Land */}
                    {hasPrint && deliveryType === "delivery" && shippingAmount > 0 && (
                      <div className="space-y-3 border-t pt-4">
                        <div className="flex justify-between items-center">
                          <Label className="text-muted-foreground">{t.checkout.shippingCost || "Versandkosten"}</Label>
                          <span className="font-medium">{formatAmount(shippingAmount)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Automatisch berechnet für {country}
                        </p>
                      </div>
                    )}

                    {/* Billing Address */}
                    <div className="flex items-center space-x-2 border-t pt-4">
                      <Checkbox
                        id="differentBillingAddress"
                        checked={differentBillingAddress}
                        onCheckedChange={(checked) => setDifferentBillingAddress(!!checked)}
                      />
                      <Label
                        htmlFor="differentBillingAddress"
                        className="cursor-pointer font-normal"
                      >
                        {t.checkout.differentBillingAddress}
                      </Label>
                    </div>
                  </>
                )}

                {/* Billing address fields */}
                {differentBillingAddress && (
                  <>
                    <div className="space-y-3">
                      <Label htmlFor="billingAddressLine1">{t.checkout.billingAddressLine1}</Label>
                      <Input
                        id="billingAddressLine1"
                        value={billingAddressLine1}
                        onChange={(e) => setBillingAddressLine1(e.target.value)}
                        placeholder={t.checkout.addressLine1Placeholder}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="billingAddressLine2">{t.checkout.billingAddressLine2} ({t.common.optional})</Label>
                      <Input
                        id="billingAddressLine2"
                        value={billingAddressLine2}
                        onChange={(e) => setBillingAddressLine2(e.target.value)}
                        placeholder={t.checkout.addressLine2Placeholder}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <Label htmlFor="billingPostalCode">{t.checkout.billingPostalCode}</Label>
                        <Input
                          id="billingPostalCode"
                          value={billingPostalCode}
                          onChange={(e) => setBillingPostalCode(e.target.value)}
                          placeholder={t.checkout.postalCodePlaceholder}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="billingCity">{t.checkout.billingCity}</Label>
                        <Input
                          id="billingCity"
                          value={billingCity}
                          onChange={(e) => setBillingCity(e.target.value)}
                          placeholder={t.checkout.cityPlaceholder}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="billingCountry">{t.checkout.billingCountry}</Label>
                      <Select value={billingCountry} onValueChange={setBillingCountry}>
                        <SelectTrigger id="billingCountry">
                          <SelectValue placeholder={t.checkout.countryPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCountries && availableCountries.length > 0 ? (
                            availableCountries.map((countryName) => (
                              <SelectItem key={countryName} value={countryName}>
                                {countryName}
                              </SelectItem>
                            ))
                          ) : (
                            Array.isArray(t.settings.shippingRatesCountryOptions) ? (
                              t.settings.shippingRatesCountryOptions.map((country: string) => (
                                <SelectItem key={country} value={country}>{country}</SelectItem>
                              ))
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary & Payment */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t.checkout.orderSummary}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  {selectedPackage ? (
                    <div className="flex justify-between text-sm">
                      <span>Package: {selectedPackage.name}</span>
                      <span>{formatAmount(parseFloat(selectedPackage.price || "0"))}</span>
                    </div>
                  ) : (
                    Array.isArray(cartData?.items) && cartData.items.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span>{(item as any).imageFilename || (item as any).videoFilename || `${t.orders.image} ${item.imageId}`}</span>
                        <span>{formatAmount(pricePerImage)}</span>
                      </div>
                    ))
                  )}
                </div>

                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t.checkout.subtotal || "Zwischensumme"}</span>
                    <span>{formatAmount(subtotal)}</span>
                  </div>
                  {deliveryType === "delivery" && hasPrint && shippingAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>{t.checkout.shippingCost || "Versandkosten"}</span>
                      <span>{formatAmount(shippingAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>{t.cart.total}</span>
                    <span>{formatAmount(totalAmount)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PayPal Payment */}
            {paypalClientId && customerName && customerEmail && phone && (deliveryType === 'delivery' ? (addressLine1 && postalCode && city && country) : true) ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t.checkout.paymentMethod}</CardTitle>
                  <CardDescription>{t.checkout.payWithPayPal}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handlePayPalCheckout}
                    disabled={isProcessingPayment}
                    className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                    size="lg"
                  >
                    {isProcessingPayment ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.checkout.processing || "Processing..."}
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.68l-.04.22-.63 3.993-.032.17a.804.804 0 01-.794.679H7.72a.483.483 0 01-.477-.558L7.418 21h1.518l.95-6.02h1.385c4.678 0 7.75-2.203 8.796-6.502z"/>
                          <path d="M2.379 21.109a.524.524 0 01-.516-.605L4.36 5.498A1.195 1.195 0 015.546 4.5h8.705c1.546 0 2.858.37 3.797 1.21.45.4.766.88.983 1.44.226.586.348 1.29.348 2.155 0 .48-.042.998-.133 1.559-.78 4.847-4.226 6.995-8.94 6.995H8.523a1.195 1.195 0 00-1.187 1.003l-.93 5.91a.524.524 0 01-.517.437H2.38z" opacity=".7"/>
                        </svg>
                        {t.checkout.payWithPayPal || "Pay with PayPal"}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {!customerName || !customerEmail
                    ? t.checkout.pleaseEnterDetails
                    : t.checkout.paypalNotConfigured}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      <ShopFooter />
    </div>
  );
}
